import React from 'react';

import { ResumeContentContext } from '../../types';
import { DEFAULT_CONTENT } from '../constants';
import { createProcessingTeamSummary, getLocationPreposition } from '../helpers';
import { calculateFallbackElements } from '../shared/creditInfoHelpers';
import { brokerRenderers } from './broker';
import { userRenderers } from './user';

export const renderLoggedInContent = (context: ResumeContentContext): React.ReactNode => {
  const {
    steps,
    flags,
    guarantor,
    ownerships,
    ownershipSelected,
    isCreditInfoLocked,
    unlockCreditInfo,
    canUnlockCreditInfo,
    hasInvestmentAddress,
  } = context;
  const { isBrokerMode = false, oneLine = false, hideLastStep, firstPerson = false, isBrokerResume = false, isProcessingTeamMode = false } = flags;
  const locationPreposition = getLocationPreposition(hasInvestmentAddress);

  if (!steps.state.isSet && !guarantor.isPresent) {
    return DEFAULT_CONTENT;
  }

  const ownershipItems = Array.isArray(ownerships) ? ownerships : [];
  const isSomeoneElse = ownershipSelected?.isNew === true;

  const { ficoElementResolved, propertiesElementResolved } = calculateFallbackElements(context);

  // Contenido inicial solo con state/guarantor
  let content: React.ReactNode;
  
  if (isBrokerMode) {
    content = brokerRenderers.renderInitialContent(steps, locationPreposition);
  } else {
    content = userRenderers.renderInitialContent(
      steps,
      guarantor,
      isSomeoneElse,
      ownershipItems,
      isCreditInfoLocked,
      canUnlockCreditInfo,
      ficoElementResolved,
      propertiesElementResolved,
      unlockCreditInfo,
      locationPreposition,
      context
    );
  }

  if (!steps.loanType.isSet) {
    return content;
  }

  // Con loanType
  if (isBrokerMode) {
    content = brokerRenderers.renderWithLoanType(steps, locationPreposition);
  } else {
    content = userRenderers.renderWithLoanType(
      steps,
      guarantor,
      isSomeoneElse,
      ownershipItems,
      isCreditInfoLocked,
      canUnlockCreditInfo,
      ficoElementResolved,
      propertiesElementResolved,
      unlockCreditInfo,
      locationPreposition,
      context
    );
  }

  if (!steps.loanOwner.isSet) {
    return content;
  }

  // Con loanOwner
  if (isBrokerMode) {
    // Para broker en step-section resume processing, mostrar formato expandido directamente
    // (renderExpanded ahora puede manejar sin propertyType usando "property" como fallback)
    content = brokerRenderers.renderExpanded(
      steps,
      guarantor,
      isSomeoneElse,
      ficoElementResolved,
      propertiesElementResolved,
      locationPreposition
    );
  } else {
    content = userRenderers.renderWithLoanOwner(
      steps,
      guarantor,
      isSomeoneElse,
      ownershipItems,
      isCreditInfoLocked,
      canUnlockCreditInfo,
      ficoElementResolved,
      propertiesElementResolved,
      unlockCreditInfo,
      locationPreposition,
      context
    );
  }

  if (!steps.propertyType.isSet) {
    // Para broker, no retornar aquí ya que ya mostramos renderExpanded que puede manejar sin propertyType
    if (!isBrokerMode) {
      return content;
    }
    // Para broker, continuar para mostrar el contenido expandido incluso sin propertyType
  } else {
    // Con propertyType
    if (isBrokerMode) {
      // Actualizar el contenido expandido ahora que tenemos propertyType
      const compactLayout = oneLine && !isCreditInfoLocked;
      if (!compactLayout) {
        content = brokerRenderers.renderExpanded(
          steps,
          guarantor,
          isSomeoneElse,
          ficoElementResolved,
          propertiesElementResolved,
          locationPreposition
        );
      }
    } else {
      content = userRenderers.renderWithPropertyType(steps, guarantor, locationPreposition);
    }
  }

  const compactLayout = oneLine && !isCreditInfoLocked;

  // Con FICO score
  if (isBrokerMode) {
    if (compactLayout && steps.propertyType.isSet) {
      content = brokerRenderers.renderCompact(steps, ficoElementResolved, locationPreposition);
    }
    // Si no es compact layout, ya tenemos renderExpanded que incluye la información de FICO
  } else {
    content = compactLayout
      ? userRenderers.renderCompact(steps, guarantor, ficoElementResolved, locationPreposition)
      : userRenderers.renderExpanded(steps, guarantor, ficoElementResolved, locationPreposition);
  }

  if (!steps.propertiesNegotiated.isSet || hideLastStep) {
    return content;
  }

  // Contenido completo
  if (!compactLayout) {
    if (!isBrokerMode) {
      content = userRenderers.renderCompleteExpanded(
        steps,
        guarantor,
        isSomeoneElse,
        ownershipItems,
        isCreditInfoLocked,
        canUnlockCreditInfo,
        ficoElementResolved,
        propertiesElementResolved,
        unlockCreditInfo,
        locationPreposition,
        context
      );
    }

    if (isProcessingTeamMode) {
      return createProcessingTeamSummary(context, locationPreposition);
    }

    return content;
  }

  // Contenido completo en una línea
  if (isBrokerMode) {
    content = brokerRenderers.renderCompleteOneLine(
      steps,
      isBrokerResume,
      ficoElementResolved,
      propertiesElementResolved,
      locationPreposition
    );
  } else if (firstPerson) {
    content = userRenderers.renderCompleteOneLineFirstPerson(
      steps,
      guarantor,
      ficoElementResolved,
      propertiesElementResolved,
      locationPreposition
    );
  } else {
    content = userRenderers.renderCompleteOneLineNormal(
      steps,
      guarantor,
      ficoElementResolved,
      propertiesElementResolved,
      locationPreposition
    );
  }

  if (isProcessingTeamMode) {
    return createProcessingTeamSummary(context, 'at');
  }

  return content;
};

